import React from 'react';
import {
  getMatchTeamRefs,
  resolveLineupSidesByRefs,
} from '../../utils/matchTeamRefUtils';
import {
  applyFormationToPitchPlayers,
  parseFormationString,
} from './formationUtils';
import {
  IconYellowCard,
  IconRedCard,
  IconGoal,
  IconDiag2y,
} from '../components/EventIcons/EventIcons';

const HOME_FORMATION_FALLBACK = [3, 3, 4];
const AWAY_FORMATION_FALLBACK = [4, 3, 3];
const DOUBLE_YELLOW_RED_EVENT_TYPES = new Set([
  'SECOND_YELLOW',
  'RED_2Y',
  'SECOND_YELLOW_RED_CARD',
]);

function isDoubleYellowRedEvent(type) {
  return DOUBLE_YELLOW_RED_EVENT_TYPES.has(type);
}

function normalizeMatchDetailsList(playerMatchDetails) {
  if (Array.isArray(playerMatchDetails)) return playerMatchDetails.filter(Boolean);
  if (playerMatchDetails && typeof playerMatchDetails === 'object') return [playerMatchDetails];
  return [];
}

/**
 * Usa siempre `lineup.formation` del servicio cuando es válida (10 jugadores de campo).
 * Solo si falta o es inválida, deriva DF-MF-FW desde `startingXI`.
 */
function pickFormationString(lineup) {
  const raw =
    lineup?.formation != null && String(lineup.formation).trim() !== ''
      ? String(lineup.formation).trim()
      : '';
  if (raw) {
    const parsed = parseFormationString(raw);
    if (parsed && parsed.reduce((a, b) => a + b, 0) === 10) {
      return raw;
    }
  }

  const xi = lineup?.startingXI;
  if (!Array.isArray(xi) || xi.length < 2) {
    return raw || '4-3-3';
  }
  const field = xi.filter((p) => p.position !== 'GK');
  if (field.length !== 10) {
    return raw || '4-3-3';
  }
  const c = { DF: 0, MF: 0, FW: 0 };
  field.forEach((p) => {
    const k = p.position;
    if (c[k] != null) c[k] += 1;
  });
  if (c.DF + c.MF + c.FW !== 10) {
    return raw || '4-3-3';
  }
  return `${c.DF}-${c.MF}-${c.FW}`;
}

function detailToPitchFlags(d) {
  const list = normalizeMatchDetailsList(d);
  if (list.length === 0) return {};

  let yellow = false;
  let red = false;
  let redCard2y = false;
  let subOut = false;
  let goalCount = 0;

  list.forEach((evt) => {
    const t = evt?.type;
    if (!t) return;
    if (t === 'YELLOW_CARD') yellow = true;
    if (isDoubleYellowRedEvent(t)) {
      redCard2y = true;
      yellow = true;
    }
    if (t === 'RED_CARD') red = true;
    if (t === 'GOAL' || t === 'OWN_GOAL') goalCount += 1;
    if (t === 'SUBSTITUTION_OUT') subOut = true;
  });

  return {
    ...(yellow ? { yellow: true } : {}),
    ...(red ? { red: true } : {}),
    ...(redCard2y ? { redCard2y: true } : {}),
    ...(subOut ? { subOut: true } : {}),
    ...(goalCount > 0 ? { goal: true, goalCount } : {}),
  };
}

function detailToListEvents(detail, idPrefix) {
  const list = normalizeMatchDetailsList(detail);
  if (list.length === 0) return [];

  let yellow = false;
  let red = false;
  let red2y = false;
  let goalCount = 0;

  list.forEach((evt) => {
    const t = evt?.type;
    if (!t) return;
    if (t === 'YELLOW_CARD') yellow = true;
    if (isDoubleYellowRedEvent(t)) red2y = true;
    if (t === 'RED_CARD') red = true;
    if (t === 'GOAL' || t === 'OWN_GOAL') goalCount += 1;
  });

  const out = [];
  if (goalCount > 0) {
    out.push(<IconGoal key={`${idPrefix}-g`} n={goalCount} />);
  }
  if (red2y) {
    if (yellow) {
      out.push(<IconYellowCard key={`${idPrefix}-y`} />);
    }
    out.push(<IconDiag2y key={`${idPrefix}-2y`} />);
  } else {
    if (yellow) {
      out.push(<IconYellowCard key={`${idPrefix}-y`} />);
    }
    if (red) {
      out.push(<IconRedCard key={`${idPrefix}-r`} />);
    }
  }

  return out;
}

function substitutionLineText(detail) {
  const list = normalizeMatchDetailsList(detail);
  if (list.length === 0) return null;

  const subs = list
    .filter((evt) => evt?.type === 'SUBSTITUTION_OUT' || evt?.type === 'SUBSTITUTION_IN')
    .slice()
    .sort((a, b) => {
      const am = a?.minute ?? Number.POSITIVE_INFINITY;
      const bm = b?.minute ?? Number.POSITIVE_INFINITY;
      return am - bm;
    });

  const evt = subs[0];
  if (!evt) return null;

  const t = evt.type;
  const relName = evt.relatedPlayer?.name;
  if (t === 'SUBSTITUTION_OUT' && evt.minute != null && relName) {
    // Quien entra por el titular que sale (texto bajo alineación titular).
    return `↓ ${evt.minute}' Entra: ${relName}`;
  }
  if (t === 'SUBSTITUTION_IN' && evt.minute != null && relName) {
    // Sustituto: relación con quien salió.
    return `↑ ${evt.minute}' Sale: ${relName}`;
  }
  return null;
}

function toPitchBasePlayer(p, isHome) {
  return {
    name: p.name || '',
    photo: p.photo || undefined,
    isHome,
    ...detailToPitchFlags(p.playerMatchDetails),
  };
}

function padNum(n) {
  if (n == null) return '00';
  return String(n).padStart(2, '0');
}

function buildStarterTableRows(homeLineup, awayLineup) {
  const a = homeLineup?.startingXI || [];
  const b = awayLineup?.startingXI || [];
  const n = 11;
  const rows = [];
  for (let i = 0; i < n; i += 1) {
    const ph = a[i];
    const pa = b[i];
    const subH = substitutionLineText(ph?.playerMatchDetails);
    const subA = substitutionLineText(pa?.playerMatchDetails);
    rows.push({
      key: `st-${i}`,
      numHome: padNum(ph?.number ?? i + 1),
      numAway: padNum(pa?.number ?? i + 1),
      nameHome: ph?.name || '—',
      nameAway: pa?.name || '—',
      eventsHome: detailToListEvents(ph?.playerMatchDetails, `h-${i}`),
      eventsAway: detailToListEvents(pa?.playerMatchDetails, `a-${i}`),
      subHome: subH,
      subAway: subA,
    });
  }
  return rows;
}

function buildBenchTableRows(homeLineup, awayLineup) {
  const a = Array.isArray(homeLineup?.substitutes) ? homeLineup.substitutes : [];
  const b = Array.isArray(awayLineup?.substitutes) ? awayLineup.substitutes : [];
  if (a.length === 0 && b.length === 0) return [];
  const m = Math.max(a.length, b.length) || 1;
  const rows = [];
  for (let i = 0; i < m; i += 1) {
    const ph = a[i];
    const pa = b[i];
    const subH = ph ? substitutionLineText(ph?.playerMatchDetails) : null;
    const subA = pa ? substitutionLineText(pa?.playerMatchDetails) : null;
    rows.push({
      key: `bn-${i}`,
      numHome: padNum(ph?.number ?? i + 1),
      numAway: padNum(pa?.number ?? i + 1),
      nameHome: ph?.name || '—',
      nameAway: pa?.name || '—',
      eventsHome: detailToListEvents(ph?.playerMatchDetails, `hs-${i}`),
      eventsAway: detailToListEvents(pa?.playerMatchDetails, `as-${i}`),
      subHome: subH,
      subAway: subA,
    });
  }
  return rows;
}

function isStarterPlayer(player) {
  return player?.isStarter !== false;
}

function mapApiLineupPlayer(player, position) {
  return {
    playerId: player.playerId,
    name: player.name || '',
    number: player.number,
    photo: player.photo ?? player.image ?? undefined,
    position,
    playerMatchDetails: player.playerMatchDetails,
  };
}

function collectStartingXI(block) {
  if (!block) return [];
  const groups = [
    [block.goalkeeper, 'GK'],
    [block.defenders, 'DF'],
    [block.midfielders, 'MF'],
    [block.forwards, 'FW'],
  ];
  const startingXI = [];
  groups.forEach(([group, position]) => {
    (Array.isArray(group) ? group : []).forEach((player) => {
      if (isStarterPlayer(player)) {
        startingXI.push(mapApiLineupPlayer(player, position));
      }
    });
  });
  return startingXI;
}

function normalizeLineupBlock(block) {
  if (!block) return null;
  if (Array.isArray(block.startingXI) && block.startingXI.length > 0) {
    return block;
  }
  if (
    block.goalkeeper ||
    block.defenders ||
    block.midfielders ||
    block.forwards
  ) {
    return {
      ...block,
      startingXI: collectStartingXI(block),
      substitutes: Array.isArray(block.substitutes) ? block.substitutes : [],
    };
  }
  return block;
}

function resolveLineupSides(list, teamRefs) {
  const first = list[0];
  if (first?.homeTeam || first?.awayTeam) {
    const homeBundle = Array.isArray(first.homeTeam) ? first.homeTeam[0] : first.homeTeam;
    const awayBundle = Array.isArray(first.awayTeam) ? first.awayTeam[0] : first.awayTeam;
    return {
      home: normalizeLineupBlock(
        homeBundle
          ? { ...homeBundle, teamId: homeBundle.teamId ?? teamRefs.homeCode ?? teamRefs.homeTeamId }
          : null,
      ),
      away: normalizeLineupBlock(
        awayBundle
          ? { ...awayBundle, teamId: awayBundle.teamId ?? teamRefs.awayCode ?? teamRefs.awayTeamId }
          : null,
      ),
    };
  }

  const { home: rawHome, away: rawAway } = resolveLineupSidesByRefs(list, teamRefs);

  return {
    home: normalizeLineupBlock(rawHome),
    away: normalizeLineupBlock(rawAway),
  };
}

/**
 * @returns {null|object} Datos mapeados desde `data.lineups` + `matchCard[0]`, o null.
 */
export function mapMatchDetailsToAlineaciones(matchDetailsData) {
  const list = matchDetailsData?.lineups;
  if (!Array.isArray(list) || !list.length) {
    return null;
  }
  const card = matchDetailsData?.matchCard?.[0];
  const md = card?.summary?.matchDetails;
  const teamRefs = getMatchTeamRefs(md);
  const hTeam = md?.home?.team;
  const aTeam = md?.away?.team;

  const { home: lHome, away: lAway } = resolveLineupSides(list, teamRefs);

  if (!lHome) return null;
  if (!lAway) return null;

  const hXi = lHome.startingXI || [];
  const aXi = lAway.startingXI || [];
  if (hXi.length !== 11 || aXi.length !== 11) {
    return null;
  }

  const homeFormation = pickFormationString(lHome);
  const awayFormation = pickFormationString(lAway);

  const homeFormationLines = parseFormationString(homeFormation) || HOME_FORMATION_FALLBACK;
  const awayFormationLines = parseFormationString(awayFormation) || AWAY_FORMATION_FALLBACK;

  const hBase = hXi.map((p) => toPitchBasePlayer(p, true));
  const aBase = aXi.map((p) => toPitchBasePlayer(p, false));

  const homePitch = applyFormationToPitchPlayers(
    hBase,
    homeFormation,
    true,
    HOME_FORMATION_FALLBACK
  );
  const awayPitch = applyFormationToPitchPlayers(
    aBase,
    awayFormation,
    false,
    AWAY_FORMATION_FALLBACK
  );

  return {
    homeFormation,
    awayFormation,
    homeFormationLines,
    awayFormationLines,
    homePitchPlayers: homePitch,
    awayPitchPlayers: awayPitch,
    homeTeam: {
      name: hTeam?.name || lHome.teamId,
      teamId: hTeam?.teamId || lHome.teamId,
      code: hTeam?.code || lHome.teamId,
      flagUrl: hTeam?.flagUrl,
      primaryHex: hTeam?.primaryHex,
    },
    awayTeam: {
      name: aTeam?.name || lAway.teamId,
      teamId: aTeam?.teamId || lAway.teamId,
      code: aTeam?.code || lAway.teamId,
      flagUrl: aTeam?.flagUrl,
      primaryHex: aTeam?.primaryHex,
    },
    homeCoach: lHome.coach?.name || '—',
    awayCoach: lAway.coach?.name || '—',
    homeCoachPhoto: lHome.coach?.photo || undefined,
    awayCoachPhoto: lAway.coach?.photo || undefined,
    starterRows: buildStarterTableRows(lHome, lAway),
    benchRows: buildBenchTableRows(lHome, lAway),
  };
}

